import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import API_BASE_URL from '../../config';
import './AdminDashboard.css';

const AdminDashboard = () => {
  const [stats, setStats] = useState({
    total_products: 0,
    total_orders: 0,
    total_users: 0,
    total_categories: 0,
    total_lens_options: 0,
    total_revenue: 0,
    recent_orders: [],
    low_stock_products: [],
    monthly_revenue: []
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchDashboardStats();
  }, []);

  // Safe price formatting function
  const formatPrice = (price) => {
    if (price === null || price === undefined) return '₹0.00';
    // Convert to number if it's a string
    const numPrice = typeof price === 'string' ? parseFloat(price) : price;
    // Check if it's a valid number
    if (isNaN(numPrice)) return '₹0.00';
    return `₹${numPrice.toFixed(2)}`;
  };

  const fetchDashboardStats = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE_URL}/admin/dashboard`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setStats(data.data);
        } else {
          setError(data.message);
        }
      } else {
        setError('Failed to load dashboard data');
      }
    } catch (error) {
      console.error('Error fetching dashboard:', error);
      setError('Error loading dashboard');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const colors = {
      'pending': '#f39c12',
      'processing': '#3498db',
      'shipped': '#9b59b6',
      'delivered': '#27ae60',
      'cancelled': '#e74c3c'
    };
    return <span className="status-badge" style={{ backgroundColor: colors[status] || '#95a5a6' }}>{status}</span>;
  };

  // Format date safely
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString();
    } catch (e) {
      return 'Invalid Date';
    }
  };

  if (loading) {
    return <div className="admin-loading">Loading dashboard...</div>;
  }

  if (error) {
    return <div className="admin-error">{error}</div>;
  }

  const statCards = [
    { title: 'Total Products', value: stats.total_products, icon: '👓', color: '#3498db' },
    { title: 'Total Orders', value: stats.total_orders, icon: '📦', color: '#27ae60' },
    { title: 'Total Users', value: stats.total_users, icon: '👥', color: '#9b59b6' },
    { title: 'Total Categories', value: stats.total_categories, icon: '📁', color: '#f39c12' },
    { title: 'Lens Options', value: stats.total_lens_options, icon: '🔍', color: '#e74c3c' },
    { title: 'Total Revenue', value: formatPrice(stats.total_revenue), icon: '💰', color: '#2c3e50' }
  ];

  // Calculate max revenue for chart
  const maxRevenue = Math.max(...(stats.monthly_revenue?.map(item => item.revenue) || [0]), 1);

  return (
    <div className="admin-dashboard">
      <div className="stats-grid">
        {statCards.map((card, index) => (
          <div key={index} className="stat-card" style={{ borderLeftColor: card.color }}>
            <div className="stat-icon" style={{ background: `${card.color}20`, color: card.color }}>
              {card.icon}
            </div>
            <div className="stat-info">
              <h3>{card.title}</h3>
              <p className="stat-value">{card.value}</p>
            </div>
          </div>
        ))}
      </div>

      {stats.monthly_revenue && stats.monthly_revenue.length > 0 && (
        <div className="chart-section">
          <h2>Monthly Revenue</h2>
          <div className="bar-chart">
            {stats.monthly_revenue.map((item, index) => (
              <div key={index} className="bar-item">
                <div className="bar-label">{item.month}</div>
                <div className="bar-wrapper">
                  <div 
                    className="bar" 
                    style={{ 
                      height: `${Math.max((item.revenue / maxRevenue) * 100, 5)}%`,
                      backgroundColor: '#3498db'
                    }}
                  ></div>
                </div>
                <div className="bar-value">{formatPrice(item.revenue)}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="two-columns">
        <div className="recent-orders-card">
          <div className="card-header">
            <h2>Recent Orders</h2>
            <Link to="/admin/orders" className="view-all">View All →</Link>
          </div>
          <table className="data-table">
            <thead>
              <tr>
                <th>Order ID</th>
                <th>Customer</th>
                <th>Date</th>
                <th>Total</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {stats.recent_orders && stats.recent_orders.length > 0 ? (
                stats.recent_orders.map(order => (
                  <tr key={order.id}>
                    <td>#{order.order_number}</td>
                    <td>{order.first_name || 'N/A'} {order.last_name || ''}</td>
                    <td>{formatDate(order.created_at)}</td>
                    <td>{formatPrice(order.total_amount)}</td>
                    <td>{getStatusBadge(order.order_status)}</td>
                  </tr>
                ))
              ) : (
                <tr><td colSpan="5" style={{ textAlign: 'center' }}>No orders found</td></tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="low-stock-card">
          <div className="card-header">
            <h2>Low Stock Products</h2>
            <Link to="/admin/products" className="view-all">View All →</Link>
          </div>
          <table className="data-table">
            <thead>
              <tr>
                <th>Product</th>
                <th>Brand</th>
                <th>Stock</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {stats.low_stock_products && stats.low_stock_products.length > 0 ? (
                stats.low_stock_products.map(product => (
                  <tr key={product.id}>
                    <td>{product.name}</td>
                    <td>{product.brand || 'N/A'}</td>
                    <td className="low-stock">{product.stock_quantity}</td>
                    <td><span className="status-badge warning">Low Stock</span></td>
                  </tr>
                ))
              ) : (
                <tr><td colSpan="4" style={{ textAlign: 'center' }}>No low stock products</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="quick-actions">
        <h2>Quick Actions</h2>
        <div className="actions-grid">
          <Link to="/admin/products/add" className="action-btn"><span>➕</span> Add Product</Link>
          <Link to="/admin/categories/add" className="action-btn"><span>📁</span> Add Category</Link>
          <Link to="/admin/coupons/add" className="action-btn"><span>🏷️</span> Create Coupon</Link>
          <Link to="/admin/orders" className="action-btn"><span>📦</span> Manage Orders</Link>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;